import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/Button';
import { fetchProductById } from '../api/products';
import { useCheckout } from '../context/CheckoutContext';
import { formatIdrFromUsd } from '../utils/currency';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { updateCheckoutData } = useCheckout();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImage, setCurrentImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState('');
  
  // Fetch product data
  useEffect(() => {
    const getProduct = async () => {
      try {
        setLoading(true);
        const data = await fetchProductById(id);
        
        if (!data) {
          setError('Product not found');
        } else {
          setProduct(data);
          // Set default selected size if available
          if (data.sizes && data.sizes.length > 0) {
            setSelectedSize(data.sizes[0].sizeInCm || '');
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Failed to load product. Please try again later.');
        setLoading(false);
      }
    };
    
    getProduct();
  }, [id]);
  
  // Handle image navigation
  const nextImage = () => {
    if (!product || !product.images || product.images.length <= 1) return;
    setCurrentImage((prev) => (prev + 1) % product.images.length);
  };
  
  const prevImage = () => {
    if (!product || !product.images || product.images.length <= 1) return;
    setCurrentImage((prev) => (prev - 1 + product.images.length) % product.images.length);
  };
  
  // Loading state
  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-light">Loading product details...</p>
        </div>
      </Layout>
    );
  }
  
  // Error or product not found
  if (error || !product) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="font-serif text-3xl font-bold text-primary mb-6">Product Not Found</h1>
          <p className="text-light mb-8">The product you're looking for doesn't exist or has been removed.</p>
          <Button onClick={() => navigate('/products')} variant="primary">
            Back to Products
          </Button>
        </div>
      </Layout>
    );
  }
  
  const { 
    name, 
    price, 
    description, 
    images, 
    material, 
    color, 
    crownStyle, 
    brimSize, 
    sizes, 
    inStock 
  } = product;
  
  return (
    <Layout>
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Images */}
          <div>
            <div className="relative aspect-square overflow-hidden bg-black border border-gray-800 mb-4">
              {images && images.length > 0 ? (
                <img 
                  src={images[currentImage]} 
                  alt={`${name} - View ${currentImage + 1}`} 
                  className="w-full h-full object-cover object-center"
                  onError={(e) => {
                    e.target.src = 'https://placehold.co/800x800/333/666?text=Image+Not+Found';
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-800">
                  <p className="text-gray-400">No image available</p>
                </div>
              )}
              
              {!inStock && (
                <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                  <span className="bg-red-900 text-white px-6 py-3 font-bold uppercase tracking-wider transform -rotate-12 text-xl">
                    Sold Out
                  </span>
                </div>
              )}
              
              {/* Image navigation arrows */}
              {images && images.length > 1 && (
                <>
                  <button 
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/80 text-white w-10 h-10 rounded-full flex items-center justify-center"
                    aria-label="Previous image"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button 
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/80 text-white w-10 h-10 rounded-full flex items-center justify-center"
                    aria-label="Next image"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}
            </div>
            
            {/* Thumbnail gallery */}
            {images && images.length > 1 && (
              <div className="grid grid-cols-5 gap-2">
                {images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImage(index)}
                    className={`aspect-square border ${
                      currentImage === index 
                        ? 'border-primary' 
                        : 'border-gray-800 opacity-70 hover:opacity-100'
                    }`}
                  >
                    <img 
                      src={img} 
                      alt={`${name} - Thumbnail ${index + 1}`} 
                      className="w-full h-full object-cover object-center"
                      onError={(e) => {
                        e.target.src = 'https://placehold.co/100x100/333/666?text=Error';
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Product Info */}
          <div>
            <h1 className="font-serif text-3xl md:text-4xl font-bold text-primary mb-2">
              {name}
            </h1>
            
            <div className="flex items-center mb-6">
              <span className="text-2xl font-bold text-primary mr-4">
                {formatIdrFromUsd(price)}
              </span>
              {!inStock && (
                <span className="bg-red-900/20 text-red-400 px-3 py-1 rounded-full text-sm font-medium">
                  Out of Stock
                </span>
              )}
            </div>
            
            <div className="prose prose-invert max-w-none mb-8">
              <p className="text-light">{description}</p>
            </div>
            
            {/* Product Specifications */}
            <div className="mb-8">
              <h3 className="font-serif text-xl font-bold text-primary mb-4">Specifications</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-secondary p-4 border border-gray-800">
                  <span className="block text-sm text-gray-400 mb-1">Material</span>
                  <span className="text-light">{material || 'Not specified'}</span>
                </div>
                <div className="bg-secondary p-4 border border-gray-800">
                  <span className="block text-sm text-gray-400 mb-1">Color</span>
                  <span className="text-light">{color || 'Not specified'}</span>
                </div>
                <div className="bg-secondary p-4 border border-gray-800">
                  <span className="block text-sm text-gray-400 mb-1">Crown Style</span>
                  <span className="text-light">{crownStyle || 'Not specified'}</span>
                </div>
                <div className="bg-secondary p-4 border border-gray-800">
                  <span className="block text-sm text-gray-400 mb-1">Brim Size</span>
                  <span className="text-light">{brimSize || 'Not specified'}</span>
                </div>
              </div>
            </div>
            
            {/* Size Selection */}
            {sizes && sizes.length > 0 && (
              <div className="mb-8">
                <h3 className="font-serif text-xl font-bold text-primary mb-4">Available Sizes</h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {sizes.map((size, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedSize(size.sizeInCm)}
                      className={`px-4 py-2 border ${
                        selectedSize === size.sizeInCm
                          ? 'bg-primary text-dark border-primary'
                          : 'bg-secondary text-light border-gray-800 hover:border-primary'
                      }`}
                    >
                      {size.sizeInCm}cm
                    </button>
                  ))}
                </div>
                <div className="text-sm text-gray-400">
                  <p>
                    Selected size: {selectedSize ? `${selectedSize}cm` : 'Please select a size'}
                  </p>
                </div>
              </div>
            )}
            
              {/* Order Now Button */}
            <div className="mb-8">
              <Button 
                variant="primary" 
                size="lg" 
                className="w-full"
                disabled={!inStock || (sizes && sizes.length > 0 && !selectedSize)}
                onClick={() => {
                  // Update checkout data in context
                  updateCheckoutData({
                    productName: name,
                    productId: id,
                    selectedSize: selectedSize,
                    price: price
                  });
                  
                  // Navigate to checkout confirmation
                  navigate('/checkout/confirmation');
                }}
              >
                {!inStock ? 'Sold Out' : (sizes && sizes.length > 0 && !selectedSize) ? 'Please Select a Size' : 'Order Now'}
              </Button>
              {!inStock && (
                <p className="text-sm text-gray-400 mt-2 text-center">
                  This item is currently out of stock. Please check back later.
                </p>
              )}
              <p className="text-sm text-gray-400 mt-2 text-center">
                Each hat is made to order. Click "Order Now" to continue to secure checkout in IDR.
              </p>
            </div>
            
            {/* Additional Info */}
            <div className="border-t border-gray-800 pt-6">
              <h3 className="font-serif text-xl font-bold text-primary mb-4">Handcrafted in Bali</h3>
              <p className="text-light text-sm">
                Each hat is meticulously crafted by skilled artisans in Bali, Indonesia. 
                Due to the handmade nature of our products, slight variations may occur, 
                making each piece uniquely yours.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProductDetail;
